import React from 'react';
import { Button, type Variant } from './Button';

// PageHeader — the standardized title + top action bar for a list/management page.
//
// Why (see docs/ui-audit.md): every page hand-rolled
//   <div className="tw-flex tw-justify-between tw-items-center tw-mb-4"><h1>…</h1><div className="tw-flex tw-gap-2">…
// with its own variants, sizes and icon spacing. That produced: Refresh rendered as
// `variant="success"` (green — semantically "succeeded", not "reload") on several pages, Export as
// `outline-secondary` on some and `outline-primary` on others, no `size` (so 40px buttons next to
// the 32px ones elsewhere), and no responsive treatment — four full-label buttons wrap badly on a
// phone.
//
// This standardizes:
//   • one row, title left, actions right, wrapping to their own line on narrow screens
//   • every action is `size="sm"` with an icon, and its label collapses below `sm` so the bar
//     becomes a compact icon strip on phones (title/aria-label preserved)
//   • semantic variants by intent (see ACTION_INTENT) rather than per-page choice
//   • the primary/create action is always rendered last, i.e. nearest the thumb
export type PageActionIntent = 'primary' | 'secondary' | 'neutral' | 'danger';

/** Intent → Button variant. Keeps colour meaning consistent across pages. */
const ACTION_INTENT: Record<PageActionIntent, Variant> = {
  primary: 'primary',            // the page's create/main action
  secondary: 'outline-primary',  // meaningful but not the main action (Search, Import)
  neutral: 'outline-secondary',  // chrome: Refresh, Export, Print
  danger: 'outline-danger',
};

export interface PageAction {
  key: string;
  label: string;
  /** Bootstrap icon class, e.g. 'bi-plus-lg'. */
  icon: string;
  onClick: () => void;
  intent?: PageActionIntent;
  /** Renders as a filled/active control (e.g. Search while the search row is open). */
  active?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  /** Overrides the derived variant when a page genuinely needs something else. */
  variant?: Variant;
  /** Keep the label visible on phones too (default: collapse to icon-only). */
  alwaysShowLabel?: boolean;
  title?: string;
}

export interface PageHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: PageAction[];
  /** Extra content between the title and the actions (e.g. a SegmentedControl). */
  children?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions = [],
  children,
  className = '',
}) => {
  const visible = actions.filter((a) => !a.hidden);
  // The main action goes last so it sits closest to the thumb on touch.
  const ordered = [
    ...visible.filter((a) => (a.intent ?? 'neutral') !== 'primary'),
    ...visible.filter((a) => (a.intent ?? 'neutral') === 'primary'),
  ];

  return (
    <div
      className={`tw-mb-3 tw-flex tw-shrink-0 tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between ${className}`}
    >
      <div className="tw-min-w-0">
        <h1 className="tw-mb-0 tw-truncate tw-text-2xl tw-font-semibold tw-text-content">{title}</h1>
        {subtitle && <p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">{subtitle}</p>}
      </div>

      {children}

      {ordered.length > 0 && (
        // justify-end matters on a phone. Above `sm` the container is a row and its
        // `sm:justify-between` pushes this block to the right edge; below `sm` the container is
        // a COLUMN, so that rule no longer applies on the horizontal axis and this block is a
        // full-width row whose buttons fell back to left-aligned — the reported "the buttons are
        // on the left". Aligning them here is a no-op at `sm+`, where the block is content-sized.
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
          {ordered.map((a) => {
            const intent = a.intent ?? 'neutral';
            const variant = a.variant ?? (a.active ? 'primary' : ACTION_INTENT[intent]);
            return (
              <Button
                key={a.key}
                type="button"
                size="sm"
                variant={variant}
                onClick={a.onClick}
                disabled={a.disabled}
                title={a.title ?? a.label}
                aria-label={a.label}
              >
                <i className={`bi ${a.icon} ${a.alwaysShowLabel ? 'tw-mr-2' : 'sm:tw-mr-2'}`} aria-hidden="true" />
                <span className={a.alwaysShowLabel ? '' : 'tw-hidden sm:tw-inline'}>{a.label}</span>
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
